import { useState } from "react";
import {Button, ButtonColor, Container, ContainerSize, Form, FormControlType, Heading, Text} from "@rebootui";
import CodeSnippet from "../../../components/controls/CodeSnippet";
import CodePreview from "../../../components/controls/CodePreview";
import CodeHighlighter from "../../../components/controls/CodeHighlighter";


type OverviewPageProps = {}

const OverviewPage = ({ }: OverviewPageProps) => {

    const [formData, setFormData] = useState({
        email: '',
        password: '',
        checkMeOut: false,
    });

    // Common function to handle form input changes
    const handleInputChange = (e: any) => {
        const { name, value, type } = e.target;
        // Handle checkbox inputs
        if (type === 'checkbox') {
            const checked = e.target.checked;
            setFormData(prev => ({ ...prev, [name]: checked }));
        } else {
            // Handle text, email, password, select, textarea, etc.
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    return (
        <Container size={ContainerSize.Fluid}>
            <Heading el="h1" className="fw-semibold mb-3 ls-1">
                Forms
            </Heading>
            <Text className="mb-4 lead">
                Examples and usage guidelines for form control styles, layout options, and custom components for creating a wide variety of forms.
            </Text>

            {/* OverviewPage */}
            <Heading el="h2" className="fw-medium fs-3 mt-4 mb-3">
                Overview
            </Heading>
            <Text className="mb-4">
                Bootstrap's form controls expand on our Rebooted form styles with classes. Use these classes to opt into their customized displays for a more consistent rendering across browsers and devices.
            </Text>
            <Text className="mb-4">
                Be sure to use an appropriate{' '}
                <Text el="code">type</Text> attribute on all inputs (e.g.,{' '}
                <Text el="code">email</Text> for email address or{' '}
                <Text el="code">number</Text> for numerical information) to take advantage of newer input controls like email verification, number selection, and more.
            </Text>
            <Text className="mb-4">
                Here's a quick example to demonstrate Bootstrap's form styles. Keep reading for documentation on required classes, form layout, and more.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Form>
                        <Form.Group controlId="exampleInputEmail1">
                            <Form.Label>Email address</Form.Label>
                            <Form.Control
                                type={FormControlType.Email}
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                placeholder="Enter email"
                                aria-describedby="emailHelp"
                            />
                            <Form.Text id="emailHelp" muted>
                                We'll never share your email with anyone else.
                            </Form.Text>
                        </Form.Group>

                        <Form.Group controlId="exampleInputPassword1">
                            <Form.Label>Password</Form.Label>
                            <Form.Control
                                type={FormControlType.Password}
                                name="password"
                                value={formData.password}
                                onChange={handleInputChange}
                                placeholder="Password"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                label="Check me out"
                                id="exampleCheck1"
                                name="checkMeOut"
                                checked={formData.checkMeOut}
                                onChange={handleInputChange}
                            />
                        </Form.Group>

                        <Button type="submit" color={ButtonColor.Primary}>
                            Submit
                        </Button>
                    </Form>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {overviewCode}
                </CodeHighlighter>
            </CodeSnippet>



            {/* Disabled Forms */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Disabled forms
            </Heading>
            <Text className="mb-4">
                Add the <Text el="code">disabled</Text> boolean attribute on an input to prevent user interactions and make it appear lighter.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Form.Control
                        type={FormControlType.Text}
                        placeholder="Disabled input here..."
                        disabled
                    />
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {disabledInputCode}
                </CodeHighlighter>
            </CodeSnippet>
        </Container>
    )
}


// Code snippets
const overviewCode = `const [formData, setFormData] = useState({
  email: '',
  password: '',
  checkMeOut: false
});

const handleInputChange = (e: any) => {
  const { name, value, type } = e.target;
  if (type === 'checkbox') {
    const checked = e.target.checked;
    setFormData(prev => ({ ...prev, [name]: checked }));
  } else {
    setFormData(prev => ({ ...prev, [name]: value }));
  }
};

<Form>
  <Form.Group controlId="exampleInputEmail1">
    <Form.Label>Email address</Form.Label>
    <Form.Control
      type={FormControlType.Email}
      name="email"
      value={formData.email}
      onChange={handleInputChange}
      placeholder="Enter email"
      aria-describedby="emailHelp"
    />
    <Form.Text id="emailHelp" muted>
      We'll never share your email with anyone else.
    </Form.Text>
  </Form.Group>

  <Form.Group controlId="exampleInputPassword1">
    <Form.Label>Password</Form.Label>
    <Form.Control
      type={FormControlType.Password}
      name="password"
      value={formData.password}
      onChange={handleInputChange}
      placeholder="Password"
    />
  </Form.Group>

  <Form.Group className="mb-3">
    <Form.Check
      type="checkbox"
      label="Check me out"
      id="exampleCheck1"
      name="checkMeOut"
      checked={formData.checkMeOut}
      onChange={handleInputChange}
    />
  </Form.Group>

  <Button type="submit" color={ButtonColor.Primary}>
    Submit
  </Button>
</Form>`;


const disabledInputCode = `<Form.Control
  type={FormControlType.Text}
  placeholder="Disabled input here..."
  disabled
/>`;

export default OverviewPage;
